import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AdminUserListDTO, Role } from '@amrita/shared';
import { TopBar } from '@/components/ui/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { UserPlus, Link2, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface InviteLinkResult {
  joinUrl: string;
  expiresAt: string;
}

const ROLE_EMOJI: Record<string, string> = {
  admin: '👑',
  consultant: '👤',
  intern: '🎓',
  banned: '🚫',
  guest: '👻',
  paused: '⏸',
};

export default function AdminUsersPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('intern');

  const [showTgFree, setShowTgFree] = useState(false);
  const [tgFreeFullName, setTgFreeFullName] = useState('');
  const [tgFreeRole, setTgFreeRole] = useState<'intern' | 'consultant'>('intern');
  const [tgFreeResult, setTgFreeResult] = useState<InviteLinkResult | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery<AdminUserListDTO>({
    queryKey: ['admin-users'],
    queryFn: () => api.get<AdminUserListDTO>('/api/admin/users'),
  });

  const invite = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/api/admin/users/invite', {
        username: inviteUsername.trim().replace(/^@/, ''),
        role: inviteRole,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      setShowInvite(false);
      setInviteUsername('');
    },
  });

  const tgFreeInvite = useMutation({
    mutationFn: () =>
      api.post<{ token: string; joinUrl: string; expiresAt: string }>('/api/admin/invites', {
        fullName: tgFreeFullName.trim(),
        role: tgFreeRole,
      }),
    onSuccess: (res) => {
      setTgFreeResult({ joinUrl: res.joinUrl, expiresAt: res.expiresAt });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  function closeTgFree() {
    setShowTgFree(false);
    setTgFreeFullName('');
    setTgFreeRole('intern');
    setTgFreeResult(null);
    setCopied(false);
    tgFreeInvite.reset();
  }

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('Скопируйте ссылку вручную:', url);
    }
  }

  return (
    <div className="safe-bottom min-h-full">
      <TopBar
        title="Пользователи"
        subtitle={data ? `${data.total} всего` : undefined}
        right={
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => setShowTgFree(true)}>
              <Link2 className="w-4 h-4 mr-1" /> Без Telegram
            </Button>
            <Button size="sm" variant="primary" onClick={() => setShowInvite(true)}>
              <UserPlus className="w-4 h-4 mr-1" /> Пригласить
            </Button>
          </div>
        }
      />

      {showInvite && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4"
          onClick={() => !invite.isPending && setShowInvite(false)}
        >
          <div
            className="bg-tg-bg rounded-2xl w-full max-w-md p-4 space-y-3 shadow-xl animate-in slide-in-from-bottom"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-semibold text-lg">Пригласить пользователя</div>
            <input
              value={inviteUsername}
              onChange={(e) => setInviteUsername(e.target.value)}
              placeholder="@username"
              className="w-full h-11 px-3 bg-tg-secondaryBg rounded-xl outline-none"
              autoFocus
            />
            <div>
              <div className="text-sm text-tg-hint mb-1">Роль</div>
              <div className="grid grid-cols-4 gap-2">
                {(['admin', 'consultant', 'intern', 'guest'] as Role[]).map((r) => (
                  <Button
                    key={r}
                    size="sm"
                    variant={inviteRole === r ? 'primary' : 'secondary'}
                    onClick={() => setInviteRole(r)}
                  >
                    {r}
                  </Button>
                ))}
              </div>
            </div>
            <div className="text-xs text-tg-hint">
              Приглашённый увидит бота по @username, при первом /start автоматически получит указанную роль.
            </div>
            {invite.isError && (
              <div className="text-xs text-red-500">
                Ошибка: {(invite.error as Error).message}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button
                variant="secondary"
                onClick={() => setShowInvite(false)}
                disabled={invite.isPending}
              >
                Отмена
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                disabled={!inviteUsername.trim() || invite.isPending}
                onClick={() => invite.mutate()}
              >
                {invite.isPending ? 'Отправляю…' : 'Пригласить'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showTgFree && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4"
          onClick={() => !tgFreeInvite.isPending && closeTgFree()}
        >
          <div
            className="bg-tg-bg rounded-2xl w-full max-w-md p-4 space-y-3 shadow-xl animate-in slide-in-from-bottom"
            onClick={(e) => e.stopPropagation()}
          >
            {!tgFreeResult ? (
              <>
                <div className="font-semibold text-lg">＋ Пригласить (без Telegram)</div>
                <input
                  value={tgFreeFullName}
                  onChange={(e) => setTgFreeFullName(e.target.value)}
                  placeholder="ФИО"
                  className="w-full h-11 px-3 bg-tg-secondaryBg rounded-xl outline-none"
                  autoFocus
                />
                <div>
                  <div className="text-sm text-tg-hint mb-1">Роль</div>
                  <div className="grid grid-cols-2 gap-2">
                    {(['intern', 'consultant'] as const).map((r) => (
                      <Button
                        key={r}
                        size="sm"
                        variant={tgFreeRole === r ? 'primary' : 'secondary'}
                        onClick={() => setTgFreeRole(r)}
                      >
                        {r === 'intern' ? 'Стажёр' : 'Консультант'}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="text-xs text-tg-hint">
                  Создаёт вход по логину/паролю без Telegram — ссылку нужно передать приглашённому лично.
                </div>
                {tgFreeInvite.isError && (
                  <div className="text-xs text-red-500">
                    Не удалось создать ссылку. Проверьте ФИО и попробуйте ещё раз.
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button variant="secondary" onClick={closeTgFree} disabled={tgFreeInvite.isPending}>
                    Отмена
                  </Button>
                  <Button
                    variant="primary"
                    className="flex-1"
                    disabled={tgFreeFullName.trim().length < 2 || tgFreeInvite.isPending}
                    onClick={() => tgFreeInvite.mutate()}
                  >
                    {tgFreeInvite.isPending ? 'Создаём…' : 'Создать ссылку'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="font-semibold text-lg">Ссылка готова</div>
                <div className="text-sm text-tg-hint">
                  Действительна до {new Date(tgFreeResult.expiresAt).toLocaleDateString('ru-RU')}
                </div>
                <div className="break-all text-sm bg-tg-secondaryBg rounded-xl p-3 font-mono">
                  {tgFreeResult.joinUrl}
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="secondary" className="flex-1" onClick={closeTgFree}>
                    Готово
                  </Button>
                  <Button
                    variant="primary"
                    className="flex-1"
                    onClick={() => copyLink(tgFreeResult.joinUrl)}
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-1" /> Скопировано
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" /> Скопировать
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="p-4 space-y-2">
        {isLoading && <div className="text-tg-hint">Загрузка…</div>}
        {data?.users.map((u) => (
          <Card
            key={u.id}
            onClick={() => nav(`/admin/users/${u.id}`)}
            className="cursor-pointer hover:bg-tg-secondaryBg/70 flex items-center gap-3"
          >
            <div className="text-2xl">{ROLE_EMOJI[u.role] ?? '❓'}</div>
            <div className="flex-1 min-w-0">
              <div className="font-medium">@{u.tgUsername ?? 'no-username'}</div>
              <div className="text-xs text-tg-hint">
                {u.role}
                {u.curriculumProgress && ` · Урок ${u.curriculumProgress.day}/${u.curriculumProgress.total}`}
                {u.lastSeenAt && ` · ${new Date(u.lastSeenAt).toLocaleDateString('ru-RU')}`}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
